import type {
  HarnessStory,
  HarnessStoryDecision,
  HarnessStoryPlanSnapshot,
  HarnessStoryResource,
  HarnessStoryState,
  StoryCollection,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import type { TagSeverity } from '@flowx/designer/shared/entities/tag-severity'

export type StoryResourceAction = 'created' | 'edited' | 'removed'
export type StoryStatus = { label: string; severity: TagSeverity }

export type StoryResourceGroup = {
  action: StoryResourceAction
  heading: string
  resources: StoryCollection<HarnessStoryResource>
  /** Null means incomplete mapping; zero means a supplied, complete empty group. */
  count: number | null
}

export type StoryDependencyDisplay = {
  storyId: string
  position: number | null
  name: string | null
  names: readonly string[]
  /** Never contains an unresolved ID or an invented dependency reason. */
  label: string
  available: boolean
}

export type StoryRow = {
  story: HarnessStory
  position: number
  total: number | null
  status: StoryStatus | null
  stateAvailable: boolean
  shortCommit: string | null
  acceptance: StoryCollection<string>
  decisions: StoryCollection<HarnessStoryDecision>
  resourceGroups: readonly StoryResourceGroup[]
  dependencies: StoryCollection<StoryDependencyDisplay>
  nothingChanged: boolean
}

export type StoryProgress = {
  implemented: number | null
  total: number | null
  allImplemented: boolean
  text: string
}

export type StoryResourceTotals = Record<StoryResourceAction, number | null>

export type StoryPlanView = {
  snapshot: HarnessStoryPlanSnapshot
  goal: string | null
  rows: readonly StoryRow[]
  progress: StoryProgress
  resourceTotals: StoryResourceTotals
}

/** Execution owns these gates (scope, permissions, review, dirty state and in-flight intent).
 * Selection is deliberately absent: selecting a later story never makes it runnable. */
export type StoryEligibilityInput = {
  stories: StoryCollection<HarnessStory>
  busy: boolean
  executionBlocked: boolean
  /** A review is on screen: it is started or dismissed, not asked for again. */
  reviewOpen: boolean
  /** The branch question waits for its answer, which is what asks for the review. */
  branchQuestion: boolean
}

export type StoryEligibilityReason =
  | 'eligible'
  | 'unavailable'
  | 'empty'
  | 'complete'
  | 'implementing'
  | 'needs_you'
  | 'stopped'
  | 'busy'
  | 'execution_blocked'
  | 'branch_question'
  | 'review_open'

export type StoryEligibility = {
  nextStoryId: string | null
  canImplement: boolean
  reason: StoryEligibilityReason
}

/** Why the plan's Implement can or cannot be pressed: a story plan's next-story reasons, or a plan
 * of work items still waiting for the maker's word that the editors are saved. */
export type PlanImplementReason = StoryEligibilityReason | 'editors_unconfirmed'

export type PlanImplementEligibility = {
  canImplement: boolean
  reason: PlanImplementReason
}

export const STORY_STATE_CAN_START: Readonly<Record<HarnessStoryState, boolean>> = {
  not_started: true,
  implementing: false,
  implemented: false,
  needs_you: false,
  stopped: false,
}
